import { z } from 'zod';
export const SOURCE_DISCOVERY_SKILL_ID = 'research.source_discovery';
export const SOURCE_DISCOVERY_SKILL_VERSION = '1.2.0';
export const SOURCE_DISCOVERY_METHODOLOGY_VERSION = 'rw-source-discovery-v1.2';
export const SOURCE_DISCOVERY_POLICY_VERSION = 'rw-source-discovery-policy-v1';
export const SOURCE_DISCOVERY_POLICY = Object.freeze({
    maxQueries: 3,
    maxResultsPerQuery: 5,
    maxTotalResults: 10,
    maxPagesPerQuery: 1,
    timeoutMs: 10_000,
    maxResponseBytes: 1_000_000,
    maxCostUnits: 1,
    safeSearch: 'strict',
});
export const sourceDiscoveryCategorySchema = z.enum([
    'market',
    'audience',
    'competitor',
    'channel',
    'measurement',
    'regulation',
    'other',
]);
export const sourceDiscoveryGeographySchema = z.discriminatedUnion('scope', [
    z.object({ scope: z.literal('global') }).strict(),
    z
        .object({
        scope: z.literal('countries'),
        countryCodes: z
            .array(z.string().regex(/^[A-Z]{2}$/u))
            .min(1)
            .max(8),
    })
        .strict(),
    z.object({ scope: z.literal('named_market'), name: z.string().trim().min(2).max(120) }).strict(),
]);
export const sourceDiscoveryInputSchema = z
    .object({
    objective: z.string().trim().min(2).max(500),
    researchQuestions: z.array(z.string().trim().min(2).max(300)).min(1).max(5),
    excludeUrls: z.array(z.url().max(2_000)).max(32).optional(),
    evidenceMode: z.enum(['general', 'customer_voice']).optional(),
    category: sourceDiscoveryCategorySchema,
    geography: sourceDiscoveryGeographySchema,
    language: z.string().regex(/^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/u),
    asOf: z.iso.date(),
})
    .strict();
export const sourceDiscoveryRequestReceiptSchema = z
    .object({
    policyVersion: z.literal(SOURCE_DISCOVERY_POLICY_VERSION),
    queries: z
        .array(z.string().trim().min(2).max(300))
        .min(1)
        .max(SOURCE_DISCOVERY_POLICY.maxQueries),
    maxQueries: z.literal(SOURCE_DISCOVERY_POLICY.maxQueries),
    maxResultsPerQuery: z.literal(SOURCE_DISCOVERY_POLICY.maxResultsPerQuery),
    maxTotalResults: z.literal(SOURCE_DISCOVERY_POLICY.maxTotalResults),
    maxPagesPerQuery: z.literal(SOURCE_DISCOVERY_POLICY.maxPagesPerQuery),
    timeoutMs: z.literal(SOURCE_DISCOVERY_POLICY.timeoutMs),
    maxResponseBytes: z.literal(SOURCE_DISCOVERY_POLICY.maxResponseBytes),
    maxCostUnits: z.literal(SOURCE_DISCOVERY_POLICY.maxCostUnits),
    safeSearch: z.literal(SOURCE_DISCOVERY_POLICY.safeSearch),
})
    .strict();
export const sourceDiscoveryCandidateSchema = z
    .object({
    rank: z.number().int().min(1).max(SOURCE_DISCOVERY_POLICY.maxTotalResults),
    providerRank: z.number().int().min(1).max(100),
    query: z.string().trim().min(2).max(300),
    title: z.string().trim().min(1).max(500),
    url: z.url().max(2_000),
    snippet: z.string().trim().max(1_000),
    retrievedAt: z.iso.datetime({ offset: true }),
    publishedAt: z.iso.datetime({ offset: true }).nullable(),
    evidenceStatus: z.literal('candidate_only'),
    captureWith: z
        .object({
        skillId: z.literal('research.public_page_evidence'),
        skillVersion: z.literal('1.1.0'),
    })
        .strict(),
})
    .strict();
export const sourceDiscoveryProviderReceiptSchema = z
    .object({
    providerId: z.string().regex(/^[a-z][a-z0-9_.-]{2,99}$/u),
    configVersion: z.string().trim().min(1).max(100),
    searchedAt: z.iso.datetime({ offset: true }),
    queryCount: z.number().int().min(1).max(SOURCE_DISCOVERY_POLICY.maxQueries),
    pageCount: z.number().int().min(0).max(SOURCE_DISCOVERY_POLICY.maxQueries),
    responseBytes: z.number().int().min(0).max(SOURCE_DISCOVERY_POLICY.maxResponseBytes),
    costUnits: z.number().min(0).max(SOURCE_DISCOVERY_POLICY.maxCostUnits),
    durationMs: z.number().int().min(0).max(SOURCE_DISCOVERY_POLICY.timeoutMs),
})
    .strict();
export const sourceDiscoveryDataGapSchema = z.enum([
    'candidate_snippets_are_not_evidence',
    'candidate_publication_date_unknown',
    'duplicate_candidates_removed',
    'excluded_candidate_url_removed',
    'invalid_candidate_url_removed',
    'candidates_after_as_of_excluded',
    'provider_returned_partial_results',
    'no_candidates',
    'source_discovery_unavailable',
    'source_discovery_failed',
]);
const sourceDiscoveryBaseSchema = z.object({
    methodologyVersion: z.literal(SOURCE_DISCOVERY_METHODOLOGY_VERSION),
    input: sourceDiscoveryInputSchema,
    requestReceipt: sourceDiscoveryRequestReceiptSchema,
    candidates: z.array(sourceDiscoveryCandidateSchema).max(SOURCE_DISCOVERY_POLICY.maxTotalResults),
    mutationAuthorized: z.literal(false),
    externalActionAuthorized: z.literal(false),
});
const completedDiscoverySchema = sourceDiscoveryBaseSchema.extend({
    providerReceipt: sourceDiscoveryProviderReceiptSchema,
    dataGaps: z.array(sourceDiscoveryDataGapSchema).min(1).max(8),
});
export const sourceDiscoveryResultSchema = z.discriminatedUnion('status', [
    completedDiscoverySchema.extend({ status: z.literal('available') }).strict(),
    completedDiscoverySchema.extend({ status: z.literal('partial') }).strict(),
    sourceDiscoveryBaseSchema
        .extend({
        status: z.literal('unavailable'),
        reason: z.enum(['provider_not_configured', 'provider_temporarily_unavailable']),
        candidates: z.array(sourceDiscoveryCandidateSchema).max(0),
        dataGaps: z.array(z.literal('source_discovery_unavailable')).length(1),
    })
        .strict(),
    sourceDiscoveryBaseSchema
        .extend({
        status: z.literal('error'),
        reason: z.enum(['deadline', 'request_failed', 'response_invalid', 'policy_violation']),
        candidates: z.array(sourceDiscoveryCandidateSchema).max(0),
        dataGaps: z.array(z.literal('source_discovery_failed')).length(1),
    })
        .strict(),
]);
//# sourceMappingURL=source-discovery.js.map